import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserButton } from "src/games/shared/Button";
import TranslateApi from "src/utils/TranslateHelper";
import { Color } from "src/games/shared/ColorValue";
import { MainScene } from "./MainScene";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { HowToPlayScene } from "src/games/CollageGame/scenes/IntroductionScene";
import AudioTranslations from "src/constants/AudioTranslations";
import { GameIntroScene } from "./GameIntro";

export class InterviewScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    nextButton: PhaserButton;
    backButton: PhaserButton;
    index: number = 1;
    student: Phaser.GameObjects.Image;
    architect: Phaser.GameObjects.Image;
    box1: TiledGameObject;
    box2: TiledGameObject;
    studentDialog: Phaser.GameObjects.Container;
    architectDialog: Phaser.GameObjects.Container;

    private skyColor: number = 0x6eb8e5;

    hollandCode: "r" | "i" | "a" | "s" | "e" | "c" = "a";

    gameScene: typeof Phaser.Scene = MainScene;

    constructor() {
        super({
            key: "InterviewScene"
        });
    }

    preload(): void {
        this.load.tilemapTiledJSON('interview-map', require("/src/assets/qci/maps/collage-interview.json"));
        this.load.image("studentPilot", require("/src/assets/qci/sprites/studentPilot/student_front.png"));

        var keys = [
            "Collage.Phaser.Interview.1.Architect",
            "Collage.Phaser.Interview.1.Student",
            "Collage.Phaser.Interview.2.Architect",
            "Collage.Phaser.Interview.2.Student",
            "Collage.Phaser.Interview.3.Architect",
            "Collage.Phaser.Interview.3.Student",
            "Collage.Phaser.Interview.4.Architect",
            "Collage.Phaser.Interview.4.Student",
            "Collage.Phaser.Interview.5.Architect",
            "Collage.Phaser.Interview.5.Student",
            "Collage.Phaser.Interview.6.Architect",
            "Collage.Phaser.Interview.6.Student",
            "Collage.Phaser.Interview.7.Architect",
            "Collage.Phaser.Interview.7.Student",
        ];

        keys.forEach((key) => {
            this.load.audio(key, AudioTranslations.FindByKeyAndLanguage(key, TranslateApi.currentLanguage.code).url);
        });
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        bindAllOfThis(this, InterviewScene.prototype);

        this.index = 1;

        // Start the audio
        var studentAudio = this.sound.add("Collage.Phaser.Interview." + this.index + ".Student");
        studentAudio.play();
        var architectAudio = this.sound.add("Collage.Phaser.Interview." + this.index + ".Architect");
        architectAudio.play("", {
            delay: studentAudio.totalDuration
        });

        this.map = this.make.tilemap({ key: "interview-map" });

        let sky = this.add.image(0, 0, "pixel");
        sky.setOrigin(0);
        sky.setDisplaySize(this.gameWidth, this.gameHeight);
        sky.setTint(this.skyColor, this.skyColor, 0xffffff, 0xffffff); // Gradient sky!
        this.addObjects();
    }

    addObjects() {
        var buttonConfig = {
            smartSize: true,
            backgroundColor: Color.KUDER_PRIMARY_BUTTON,
            keyMapping: "keydown_RIGHT"
        };

        this.map.getObjectLayer("Layout").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "question") {
                this.box1 = ob;
                // One text box for the game objective and how to play instructions
                this.studentDialog = this.createDialogBox(ob, "Collage.Phaser.Interview." + this.index + ".Student");
            }
            else if (ob.name === "response") {
                this.box2 = ob;
                // One text box for the game objective and how to play instructions
                this.architectDialog = this.createDialogBox(ob, "Collage.Phaser.Interview." + this.index + ".Architect");
            }
            else if (ob.name === "architect") {
                this.architect = this.add.image(ob.x, ob.y, "npc");
                this.architect.setOrigin(0);
                this.architect.setDepth(2);
                this.architect.setDisplaySize(ob.width, ob.height);
            }
            else if (ob.name === "student") {
                this.student = this.add.image(ob.x, ob.y, "studentPilot");
                this.student.setOrigin(0);
                this.student.setDisplaySize(ob.width, ob.height);
                this.student.setDepth(3);
            }
            else if (ob.name === "terrain") {
                let terrain = this.add.image(ob.x, ob.y, "terrain");
                terrain.setScale(0.8);
                terrain.setDepth(0);
                terrain.setOrigin(0);
            }
            else if (ob.name === "next") {
                this.nextButton = PhaserButton.createQuickNextButton(this, ob.x, ob.y);
                this.nextButton.setX(this.gameWidth - this.nextButton.width - 50);
                this.nextButton.setDepth(2);
                this.nextButton.on('buttonClicked', () => {
                    this.sound.stopAll();
                    if (this.index === 7) {
                        // Now start game with the how to play scene
                        this.input.removeAllListeners();
                        this.scene.add("HowToPlay", HowToPlayScene, true);
                        this.scene.remove(this);
                    }
                    else {
                        // Update textboxes with next section of interview
                        this.index++;
                        this.updateTextboxes();
                    }
                });
            }
        });
        this.studentDialog.setY(this.student.y - this.studentDialog.displayHeight - 20);
        this.architectDialog.setY(this.architect.y - this.architectDialog.displayHeight - 20);
        this.addBackButton();
    }

    updateTextboxes() {
        this.studentDialog.destroy();
        this.architectDialog.destroy();
        this.studentDialog = this.createDialogBox(this.box1, "Collage.Phaser.Interview." + this.index + ".Student");
        this.architectDialog = this.createDialogBox(this.box2, "Collage.Phaser.Interview." + this.index + ".Architect");

        this.studentDialog.setY(this.student.y - this.studentDialog.displayHeight - 10);
        this.architectDialog.setY(this.architect.y - this.architectDialog.displayHeight - 10);

        // Start the audio
        var studentAudio = this.sound.add("Collage.Phaser.Interview." + this.index + ".Student");
        studentAudio.play();
        var architectAudio = this.sound.add("Collage.Phaser.Interview." + this.index + ".Architect");
        architectAudio.play("", {
            delay: studentAudio.totalDuration
        });
    }

    createDialogBox(ob: TiledGameObject, translationKey: string): Phaser.GameObjects.Container {
        var padding = 5;
        var fontSize = 17;
        var borderWidth = 5;
        var borderColor = Color.PLANET_COLORS.get("A");
        var textBox = this.add.container(ob.x, ob.y);
        var textStyle = { fontFamily: 'Roboto Mono, Arial', color: "#F8E71C", fontSize: fontSize, padding: 10, align: "justified" };

        var translatedText = TranslateApi.translate(translationKey);
        var text = this.add.text(padding + borderWidth, padding + borderWidth, translatedText, textStyle);
        text.setWordWrapWidth(ob.width, true);
        textBox.setSize(text.width + padding + textStyle.padding, text.height + padding + textStyle.padding);

        // Add text box background
        var textBoxBackground = this.add.image(0, 0, "pixel");
        textBoxBackground.setOrigin(0);
        textBoxBackground.setDisplaySize(textBox.displayWidth, textBox.displayHeight);
        textBoxBackground.setTint(0x000000);
        textBoxBackground.setPosition(borderWidth + padding + textStyle.padding, borderWidth + padding + textStyle.padding);

        // Add text box border
        var textBoxBorder = this.add.image(0, 0, "pixel");
        textBoxBorder.setOrigin(0);
        textBoxBorder.setDisplaySize(textBoxBackground.displayWidth + 2 * borderWidth, textBoxBackground.displayHeight + 2 * borderWidth);
        textBoxBorder.setTint(borderColor);
        textBoxBackground.setPosition(borderWidth, borderWidth);

        textBox.add(textBoxBorder);
        textBox.add(textBoxBackground);
        textBox.add(text);
        textBox.setDepth(2);

        return textBox;
    }

    addBackButton() {
        this.backButton = PhaserButton.createQuickBackButton(this, 50, this.nextButton.y);
        this.backButton.setDepth(2);
        this.backButton.on('buttonClicked', () => {
            this.sound.stopAll();
            if (this.index === 1) {
                this.input.enabled = false;
                this.scene.remove(this);
                GameIntroScene.StartFromEnd = true;
                this.scene.add("GameIntroScene", GameIntroScene, true);
                return;
            }
            else {
                // Update textboxes with previous section of interview
                this.index--;
                this.updateTextboxes();
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/games/CollageGame/scenes/InterviewScene.ts